import { Check } from "@mui/icons-material";
import { alpha, Button, Paper, Stack, useTheme } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import BoxProvider from "../../../../contexts/BoxProvider";
import { FINISH_THERAPY } from "../../../../enums/ApiPaths";
import {
  SINGLE_THERAPY_KEY,
  THERAPY_DAYS_FETCH,
} from "../../../../enums/QueryKeys";
import DoctorsLetter from "../../../appointments/singleView/components/DoctorsLetter";
import useSubmit from "../../../master/species/hooks/useSubmit";
import DialogCore from "../core/DialogCore";
import { TemplateCategory } from "../../../../enums/TemplateCategory";
import DOMPurify from "dompurify";
import ReactQuill from "react-quill";

const formId = "finish_therapy_form";

export default function FinishTherapy({
  therapy = null,
  inline = false,
  key = null,
  isArchived = false,
}) {
  const { t } = useTranslation();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const url = FINISH_THERAPY + id;

  const methods = useForm({
    defaultValues: {
      id: Number(id),
      dischargeLetter: "",
    },
  });

  React.useEffect(() => {
    if (therapy) {
      methods.reset({
        id: Number(id),
        dischargeLetter: therapy.dischargeLetter ?? "",
      });
    }
  }, [therapy, id, methods]);

  const submitId = null;
  const submittion = useSubmit(submitId, url, key);

  async function submitData(submitObject) {
    const res = await submittion.submitCallback(submitObject);
    if (res) {
      queryClient.refetchQueries([SINGLE_THERAPY_KEY, id]);
      queryClient.refetchQueries([THERAPY_DAYS_FETCH, id]);
    }
  }

  if (inline) {
    return (
      <BoxProvider>
        <FormProvider {...methods}>
          <form id={formId} onSubmit={methods.handleSubmit(submitData)}>
            {isArchived ? null : (
              <Stack direction={"row"}>
                <Button
                  type="submit"
                  variant="contained"
                  sx={{ mb: 2 }}
                  disabled={submittion.loading}
                >
                  {t("Save")}
                </Button>
              </Stack>
            )}

            {isArchived ? (
              <Paper
                elevation={1}
                sx={{
                  p: 2,
                  borderRadius: "16px",
                  height: "100%",
                }}
              >
                <div
                  style={{
                    maxHeight: "none",
                    overflowY: "clip",
                    position: "relative",
                    transition: "max-height 0.4s ease",
                  }}
                >
                  <ReactQuill
                    value={DOMPurify.sanitize(therapy.dischargeLetter ?? "")}
                    readOnly={true}
                    theme={"bubble"}
                    style={{
                      overflowWrap: "break-word",
                      display: "block",
                      width: "100%",
                    }}
                  />
                </div>
              </Paper>
            ) : (
              <DoctorsLetter
                name="dischargeLetter"
                category={TemplateCategory.THERAPY}
              />
            )}
          </form>
        </FormProvider>
      </BoxProvider>
    );
  }

  return (
    <BoxProvider>
      <FormProvider {...methods}>
        <form id={formId} onSubmit={methods.handleSubmit(submitData)}>
          <DialogCore
            title={t("FinishTherapy")}
            icon={<Check />}
            buttonSize="small"
            isIcon
            buttonTitle={t("FinishTherapy")}
            maxWidth="lg"
            formId={formId}
            closeWhen={submittion.success}
            isLoading={submittion.loading}
          >
            <DoctorsLetter
              name="dischargeLetter"
              category={TemplateCategory.THERAPY}
            />
          </DialogCore>
        </form>
      </FormProvider>
    </BoxProvider>
  );
}
